import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useResponsiveValue, Box, Columns, Column, Stack, Text, Link, IconNewWindow, Divider, Hidden } from "braid-design-system";
import { useEffect, cloneElement } from "react";
import { useTransitionState } from "react-transition-state";
import { useShouldReduceDuplicateApply } from "../JDVLayout/JDVLayout.mjs";
import { useAnalyticsHandlers } from "../CompanyProfile/CompanyProfile.mjs";
import { forceAlignWidthToSiblings, buttonBar, fixedApplyWrapper, stickyApplyButton, topApplyWrapper, transitionStyles } from "../JDVLayout/JDVLayout.css.mjs";
import { hoverLink } from "../JobDetailsView/JobDetailsView.css.mjs";
const StickyApply = ({
  layoutOverride,
  actions,
  stickApplyButtonOnDesktop,
  applyAction,
  saveAction,
  jobUrl,
  jobTitle,
  companyName,
  onAnalyticsEvent = () => {
  },
  paddingX,
  embedded,
  hideOpenInNewTab
}) => {
  const responsiveValue = useResponsiveValue();
  const isMobile = responsiveValue({
    mobile: true,
    tablet: false
  });
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const hideApply = useShouldReduceDuplicateApply();
  const [state, toggle] = useTransitionState({
    enter: true,
    exit: true,
    preEnter: true,
    preExit: true,
    mountOnEnter: true,
    unmountOnExit: true,
    timeout: 400
  });
  useEffect(() => {
    toggle(stickApplyButtonOnDesktop);
  }, [stickApplyButtonOnDesktop, toggle]);
  const desktopElement = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Box,
      {
        width: "full",
        background: "surface",
        paddingTop: !embedded ? "medium" : hideOpenInNewTab && "small" || "none",
        paddingBottom: "small",
        alignItems: "center",
        paddingX,
        className: forceAlignWidthToSiblings,
        children: /* @__PURE__ */ jsxs(Columns, { space: "xsmall", children: [
          /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "xxsmall", children: [
            /* @__PURE__ */ jsx(Text, { weight: "medium", maxLines: 1, baseline: false, children: jobUrl ? /* @__PURE__ */ jsxs(
              Link,
              {
                href: jobUrl,
                className: hoverLink,
                target: "_blank",
                onClick: analytics.onJobTitleClick,
                children: [
                  jobTitle,
                  !hideOpenInNewTab && /* @__PURE__ */ jsx(
                    Box,
                    {
                      paddingLeft: "xxsmall",
                      component: "span",
                      display: { mobile: "none", desktop: "inline" },
                      children: /* @__PURE__ */ jsx(IconNewWindow, { alignY: "lowercase" })
                    }
                  )
                ]
              }
            ) : jobTitle }),
            companyName ? /* @__PURE__ */ jsx(Text, { size: "xsmall", children: companyName }) : void 0
          ] }) }),
          applyAction && !hideApply ? /* @__PURE__ */ jsx(Column, { width: "content", children: cloneElement(applyAction, { size: "small" }) }) : null,
          saveAction ? /* @__PURE__ */ jsx(Column, { width: "content", children: cloneElement(saveAction, { size: "small" }) }) : null
        ] })
      }
    ),
    /* @__PURE__ */ jsx(Box, { paddingX, children: /* @__PURE__ */ jsx(Divider, {}) })
  ] });
  const mobileElement = hideApply ? /* @__PURE__ */ jsx(Fragment, {}) : /* @__PURE__ */ jsx(
    Box,
    {
      paddingY: "small",
      paddingX: "xsmall",
      background: "surface",
      className: buttonBar,
      width: "full",
      position: "fixed",
      bottom: 0,
      "data-testid": "mobile-sticky-button-bar",
      children: /* @__PURE__ */ jsx(Stack, { space: "large", children: cloneElement(actions, { key: "mobile actions" }) })
    }
  );
  switch (layoutOverride) {
    case "desktop":
      return /* @__PURE__ */ jsx("div", { className: topApplyWrapper, children: /* @__PURE__ */ jsx(Hidden, { print: true, children: desktopElement }) });
    case "mobile":
      return /* @__PURE__ */ jsx("div", { className: fixedApplyWrapper, children: /* @__PURE__ */ jsx(Hidden, { print: true, children: mobileElement }) });
    default:
      if (isMobile) {
        return /* @__PURE__ */ jsx("div", { className: fixedApplyWrapper, children: /* @__PURE__ */ jsx(Hidden, { print: true, children: mobileElement }) });
      }
      return /* @__PURE__ */ jsx(
        Box,
        {
          className: stickyApplyButton,
          zIndex: "sticky",
          top: 0,
          overflow: "visible",
          children: /* @__PURE__ */ jsx(Hidden, { print: true, children: state.isMounted && /* @__PURE__ */ jsx(
            Box,
            {
              className: {
                [transitionStyles.preEnter]: state.status === "preEnter",
                [transitionStyles.entering]: state.status === "entering",
                [transitionStyles.entered]: state.status === "entered",
                [transitionStyles.preExit]: state.status === "preExit",
                [transitionStyles.exiting]: state.status === "exiting",
                [transitionStyles.exited]: state.status === "exited"
              },
              children: /* @__PURE__ */ jsx("div", { className: topApplyWrapper, children: desktopElement })
            }
          ) })
        }
      );
  }
};
export {
  StickyApply
};
